import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import './ArticleDetail.css';
import { getArticleById } from '../config/articles';

const ArticleDetail = () => {
  const { id } = useParams();
  const [articleContent, setArticleContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const article = getArticleById(id);

  useEffect(() => {
    const loadArticle = async () => {
      if (!article) {
        setError('Article not found');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        // Fetch the HTML file
        const response = await fetch(article.htmlFile);
        if (!response.ok) {
          throw new Error(`Failed to load article: ${response.statusText}`);
        }
        
        let html = await response.text();
        
        // Process the HTML to fix image paths and extract body content
        // Remove DOCTYPE, html, head, and body tags, keep only the inner content
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        
        // Get the body content
        const bodyContent = doc.body.innerHTML;
        
        // Process image paths - ensure they use absolute paths from root
        // If images are relative, convert them to absolute paths in /articles/
        let processedHtml = bodyContent.replace(
          /src="([^"]+)"/g,
          (match, imagePath) => {
            // If it's a relative path (doesn't start with / or http), make it absolute
            if (!imagePath.startsWith('/') && !imagePath.startsWith('http')) {
              // Extract just the filename
              const filename = imagePath.split('/').pop();
              return `src="/articles/${filename}"`;
            }
            return match;
          }
        );
        
        setArticleContent(processedHtml);
      } catch (err) {
        console.error('Error loading article:', err);
        setError(err.message || 'Failed to load article');
      } finally {
        setLoading(false);
      }
    };

    loadArticle();
  }, [article, id]);

  if (!article) {
    return (
      <div className="article-detail">
        <div className="container">
          <h1>Article not found</h1>
          <Link to="/articles">Back to Articles</Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="article-detail">
        <div className="container">
          <div style={{ textAlign: 'center', padding: '4rem' }}>
            <i className="fas fa-spinner fa-spin" style={{ fontSize: '2rem', color: '#2563eb' }}></i>
            <p>Loading article...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="article-detail">
        <div className="container">
          <h1>Error loading article</h1>
          <p>{error}</p>
          <Link to="/articles">Back to Articles</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="article-detail">
      <article className="article-content-wrapper">
        <div className="container">
          <Link to="/articles" className="back-link">
            <i className="fas fa-arrow-left"></i> Back to Articles
          </Link>
          
          <div className="article-header">
            <div className="article-meta-top">
              <span className="article-category">{article.category}</span>
              <span className="article-date">
                <i className="fas fa-calendar"></i> {article.date}
              </span>
              <span className="article-read-time">
                <i className="fas fa-clock"></i> {article.readTime}
              </span>
            </div>
            <h1>{article.title}</h1>
            <div className="article-author">
              <i className="fas fa-user"></i> {article.author}
            </div>
            <div className="article-tags">
              {article.tags.map(tag => (
                <span key={tag} className="article-tag">{tag}</span>
              ))}
            </div>
          </div>

          <div 
            className="article-body"
            dangerouslySetInnerHTML={{ __html: articleContent }}
          />
        </div>
      </article>
    </div>
  );
};

export default ArticleDetail;
